import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { api } from '@/api/client';
import { useAuth } from '@/context/AuthContext';
import { Plus, Trash, Edit } from 'lucide-react';
import { USER_ROLES } from '@/utils/helpers';

export const Users = () => {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ username: '', pin: '', full_name: '', role: 'staff', active: true });
  const { user: currentUser, isSuperAdmin } = useAuth();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await api.getUsers();
      setUsers(res.data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await api.updateUser(editingUser.id, formData);
      } else {
        await api.createUser(formData);
      }
      loadUsers();
      closeModal();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleDelete = async (user) => {
    // Safety guard: prevent self-delete
    if (user.id === currentUser?.id) {
      alert("You cannot delete your own account while signed in.");
      return;
    }
    if (!window.confirm(`Permanently delete user "${user.full_name}" (${user.username})?\n\nThis action cannot be undone.`)) {
      return;
    }
    try {
      await api.deleteUser(user.id);
      loadUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        pin: '', // Don't pre-fill PIN
        full_name: user.full_name,
        role: user.role,
        active: user.active
      });
    } else {
      setEditingUser(null);
      setFormData({ username: '', pin: '', full_name: '', role: 'staff', active: true });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-medium tracking-tight text-[var(--text-primary)]">Users</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">Manage staff accounts and permissions.</p>
        </div>
        {isSuperAdmin() && (
          <Button onClick={() => openModal()} variant="primary" data-testid="add-user-button">
            <Plus size={14} strokeWidth={2} />
            Add User
          </Button>
        )}
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Username</TableHead>
              <TableHead>Full Name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-[var(--text-muted)]">No users found</TableCell>
              </TableRow>
            ) : (
              users.map((user) => {
                const isSelf = user.id === currentUser?.id;
                const roleVariant = user.role === 'superadmin' ? 'purple' : user.role === 'admin' ? 'info' : 'default';
                return (
                  <TableRow key={user.id}>
                    <TableCell className="font-mono text-xs">
                      {user.username}
                      {isSelf && (
                        <span className="ml-2 text-[10px] text-[var(--brand-cyan)] uppercase tracking-wider">you</span>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{user.full_name}</TableCell>
                    <TableCell><Badge variant={roleVariant}>{user.role}</Badge></TableCell>
                    <TableCell>
                      <Badge variant={user.active ? 'success' : 'danger'}>
                        {user.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        {isSuperAdmin() && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openModal(user)}
                              data-testid={`edit-user-${user.id}`}
                              title="Edit user"
                            >
                              <Edit size={14} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(user)}
                              disabled={isSelf}
                              data-testid={`delete-user-${user.id}`}
                              title={isSelf ? "You can't delete your own account" : "Delete user"}
                              className={isSelf ? '' : 'hover:!text-[var(--status-danger)] hover:!bg-[var(--status-danger)]/10'}
                            >
                              <Trash size={14} className={isSelf ? '' : 'text-[var(--status-danger)]'} />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {!isSuperAdmin() && (
        <div className="text-xs text-[var(--text-muted)] text-center py-4">
          Only super admins can create, edit, or delete users.
        </div>
      )}

      <Modal isOpen={showModal} onClose={closeModal} title={editingUser ? 'Edit User' : 'Add User'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Username"
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            required
            data-testid="user-username"
          />
          <Input
            label={editingUser ? 'New PIN (leave blank to keep current)' : 'PIN'}
            type="password"
            value={formData.pin}
            onChange={(e) => setFormData({ ...formData, pin: e.target.value })}
            required={!editingUser}
            data-testid="user-pin"
          />
          <Input
            label="Full Name"
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            required
            data-testid="user-fullname"
          />
          <Select
            label="Role"
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            data-testid="user-role"
          >
            {USER_ROLES.map(role => <option key={role} value={role}>{role}</option>)}
          </Select>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              className="w-4 h-4"
              data-testid="user-active"
            />
            <span className="text-sm text-[var(--text-primary)]">Active</span>
          </label>
          <div className="flex justify-end gap-2 pt-4 border-t border-[var(--border)]">
            <Button type="button" variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button type="submit" variant="primary" data-testid="save-user">{editingUser ? 'Update User' : 'Create User'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
